import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/affiliate-marketing")({
  head: () => {
    const h = pageHead("pmAffiliateMarketing", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Affiliate-Marketing Agentur",
          description: "Netzwerk-Setup, Partner-Akquise, Tracking-Hygiene und Provisions-Steuerung — gesteuert auf wirtschaftlichen Beitrag, nicht auf Affiliate-Reichweite.",
          url: "/leistungen/performance-marketing/affiliate-marketing",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
          { name: "Affiliate-Marketing", path: "/leistungen/performance-marketing/affiliate-marketing" },
        ])),
      ],
    };
  },
  component: AffiliateDE,
});

const steps = [
  { num: "01", title: "Analyse & Publisher-Mix-Check (3–5 Tage)", body: "Account-Audit (falls schon Affiliate-Aktivität läuft), Publisher-Mix-Analyse (welche Typen tragen, welche nicht), Tracking-Setup-Check, Provisions-Modell-Audit, Wettbewerbs-Analyse im Affiliate-Markt. Output: konkrete Hypothesen plus realistisches Erwartungs-Management." },
  { num: "02", title: "Setup, Tracking-Härtung & Netzwerk-Auswahl (1–2 Wochen)", body: "Affiliate-Tracking server-side härten (Postback-Setup, Conversion-Validation). Netzwerk-Auswahl je nach Mandate-Profil. Provisions-Modell-Definition mit New-Customer-Differenzierung. Brand-Bidding-Verbote und Last-Click-Disziplin etablieren." },
  { num: "03", title: "Publisher-Akquise & Aktivierung (ab Woche 2–3)", body: "Gezielte Akquise von Content-Publishern, Vergleichsportalen und Treueprogrammen, die zum Mandanten passen. Sortierung der bestehenden Publisher nach wirtschaftlichem Beitrag — Aufstockung für High-Performer, Reduktion oder Abschaltung bei Last-Click-Mitnehmern." },
  { num: "04", title: "Skalierung & laufender Betrieb (kontinuierlich)", body: "Monatlicher Publisher-Performance-Review. Provisions-Modell-Anpassungen je nach Markt-Entwicklung. Cross-Channel-Validierung (Affiliate vs. Search-Brand-Kannibalisierung). Operative Steuerung in der Regel direkt durch den Geschäftsführer." },
];

const capabilities = [
  { name: "Netzwerk-Auswahl & Setup", detail: "Awin · financeAds · Belboon" },
  { name: "Server-side Tracking", detail: "Postbacks · Conversion-Validation" },
  { name: "Publisher-Akquise", detail: "Content · Vergleich · Treue" },
  { name: "Provisions-Steuerung", detail: "New-Customer-Differenzierung" },
  { name: "Brand-Bidding-Schutz", detail: "Monitoring · Sanktionen" },
  { name: "Cookie-Window-Disziplin", detail: "Reale Conversion-Cycles" },
  { name: "Cross-Channel-Attribution", detail: "Integrierter Funnel-Kontext" },
  { name: "Reporting & Steuerung", detail: "Aktive Aufstockung · Abschaltung" },
];

const audience = [
  { tag: "Primär", title: "Etabliertes B2C E-Commerce mit Skalierungs-Bedarf", body: "Affiliate-Marketing funktioniert besonders gut für etablierte E-Commerce-Setups, die eine starke Marken-Position haben und Content-Publisher als Empfehlungs-Verstärker nutzen können. Sinnvoll ab Monatsumsätzen von etwa €100.000, voll-skalierbar." },
  { tag: "Sekundär", title: "Produkte mit hoher Vergleichbarkeit", body: "Affiliate funktioniert besonders gut, wo Konsumenten in einer Recherche-Phase sind — Versicherungen, Kreditkarten, Reisen, Elektronik, Software. Vergleichsportale und Content-Affiliate sind die richtigen Partner-Typen." },
  { tag: "Tertiär", title: "Marken mit Treueprogramm-Potenzial", body: "Wo Cashback und Treueprogramme zum Geschäftsmodell passen — Mode, Mobilfunk, DTC-Subscriptions. Aber Vorsicht: nicht jeder Cashback-Affiliate liefert wirtschaftlichen Beitrag." },
  { tag: "Disqualifikation", title: "Wann wir nicht der richtige Partner sind", body: "Bei Monatsumsätzen unter €50.000 — die operativen Setup- und Pflege-Kosten lohnen sich meist nicht. Wenn Affiliate als Set-and-Forget-Kanal gesehen wird. Bei reinen B2B- oder SaaS-Setups." },
];

const realistic = [
  "Provisions-Effizienz-Steigerung von 20–40 % im ersten Jahr ist realistisch, wenn vorher mit unsortiertem Publisher-Mix und ohne Last-Click-Disziplin gearbeitet wurde.",
  "Anteil neuer Customer am Affiliate-Umsatz auf 40–60 % steigerbar durch differenzierte Provisions-Modelle — viele Setups haben einen Neukunden-Anteil unter 20 %.",
  "Affiliate-Volumen-Hebel von 30–50 % durch aktive Akquise neuer Content-Publisher und Vergleichsportale ist eine typische Bandbreite.",
  "Brand-Bidding-Schutz spart 5–15 % Affiliate-Provisionen — Geld, das vorher an Affiliates floss, die nichts beitragen, weil der Customer ohnehin über Brand-Search gefunden hätte.",
];

const value = [
  ["Aktive Publisher-Steuerung statt Set-and-Forget.", "Wir reporten auf wirtschaftlichen Beitrag pro Publisher-Typ, nicht auf Affiliate-Reichweite."],
  ["Tracking-Hygiene als Pflicht-Bestandteil.", "Server-side Postbacks, Conversion-Validation, Cookie-Window-Disziplin."],
  ["Netzwerk-Agnostischer Ansatz.", "Wir empfehlen das Netzwerk, das zum Mandate passt — nicht das, mit dem wir die höchsten Volumen-Boni machen."],
  ["Brand-Bidding-Schutz systematisch.", "Klare Regeln plus Monitoring plus Sanktionen."],
  ["Operative Steuerung durch erfahrene Hand.", "Direkt durch den Geschäftsführer in den meisten Mandaten."],
  ["Vergütungs-Modell ohne Volumen-Kopplung.", "Wir verdienen nicht mehr, wenn das Affiliate-Volumen wächst."],
];

const related = [
  { to: "/leistungen/performance-marketing", name: "Performance Marketing", sub: "Affiliate als Teil des integrierten Performance-Mix" },
  { to: "/leistungen/tracking-datenbasis", name: "Tracking & Datenbasis", sub: "Saubere Attribution als Pflicht-Voraussetzung" },
  { to: "/leistungen/strategie-koordination", name: "Strategie & Koordination", sub: "Cross-Channel-Strategie inklusive Affiliate-Anteilen" },
];

const channelLinks = [
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, PMax und Demand Gen — Performance auf Googles Eigentums-Fläche." },
  { to: "/leistungen/performance-marketing/meta-ads", name: "Werbung auf Facebook & Instagram", sub: "Performance auf Instagram und Facebook mit Creative-Disziplin." },
];

function AffiliateDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Affiliate-Marketing"
          headline={<>Affiliate-Marketing Agentur — <em>Partner-Steuerung statt Provision-Lottery,</em> mit Tracking-Disziplin.</>}
          lead="Netzwerk-Setup, Partner-Akquise, Tracking-Hygiene und Provisions-Steuerung — gesteuert auf wirtschaftlichen Beitrag, nicht auf Affiliate-Reichweite. Calvarius arbeitet als Affiliate-Marketing Agentur für E-Commerce-Mandate, die Affiliate als Performance-Kanal verstehen — nicht als Set-and-Forget-Gutscheinkanal."
          metaStrip={["B2C-Fokus", "Netzwerk-agnostisch", "Tracking-Hygiene"]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "Affiliate-Marketing" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was eine Affiliate-Marketing Agentur mit <em>Disziplin</em> heute leistet.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine Affiliate-Marketing-Agentur jenseits der Standard-Netzwerk-Verwalter sucht, hat in der Regel eine kritische Beobachtung gemacht: Affiliate-Marketing wird in vielen Setups schlecht gesteuert. Provisionen fließen an Publisher, die wenig wirtschaftlichen Beitrag liefern (Last-Click-Cookie-Stuffing, Brand-Bidding-Affiliates, Cashback-Provider, die im Funnel-Endspurt einsteigen). Cookie-Lifetimes und Attribution-Modelle benachteiligen Top-of-Funnel-Publisher. Das eigentliche Performance-Potenzial des Kanals — qualifizierte Empfehlungen durch Content-Publisher und Vergleichsportale — bleibt oft ungenutzt.</p>
              <p className="mb-5">Calvarius behandelt Affiliate-Marketing als Performance-Kanal mit Disziplin auf zwei Ebenen. Erstens: <strong className="font-semibold text-ink">Partner-Steuerung</strong> — wir aktivieren gezielt die Publisher-Typen, die wirtschaftlich Beitrag liefern, und sortieren systematisch die Publisher aus, die nur Last-Click-Provisionen abgreifen. Zweitens: <strong className="font-semibold text-ink">Tracking-Disziplin</strong> — Affiliate-Tracking ist technisch komplex, fehleranfällig und in vielen Setups schlecht eingerichtet.</p>
              <p>Konkret heißt das: Wir arbeiten netzwerk-agnostisch — Awin, financeAds, Belboon, oder direktes Inhouse-Tracking, je nach Mandate-Bedarf. Wir setzen Provisions-Modelle, die wirtschaftliches Verhalten belohnen. Wir tracken über server-side Postbacks plus First-Party-Data-Validation. Und wir steuern den Affiliate-Mix als Teil des integrierten Performance-Setups — über unsere <Link to="/leistungen/strategie-koordination" className="underline">Strategie-&amp;-Koordinations-Disziplin</Link>.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Wann Sie eine Affiliate-Marketing Agentur <em>brauchen — und wann nicht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie unsere Affiliate-Marketing Agentur <em>Mandate startet.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Schneller Start als Wettbewerbs-Vorteil</p>
              <p className="text-[15px] leading-relaxed text-ink-2">Während klassische Affiliate-Agenturen oft sechs bis zwölf Wochen brauchen, bevor das erste optimierte Setup mit aktiver Publisher-Steuerung läuft, sind wir innerhalb von zwei bis drei Wochen produktiv. Möglich durch netzwerk-agnostischen Ansatz und etablierte Partner-Beziehungen.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unsere Affiliate-Marketing Agentur <em>konkret liefert.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools und Stack unserer <em>Affiliate-Steuerung.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Netzwerk-Plattformen</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Awin (international, breit)</li>
                  <li>financeAds (DACH-Finanzbereich)</li>
                  <li>Belboon (DACH-E-Commerce)</li>
                  <li>Tradedoubler (international)</li>
                  <li>Direkt-Tracking via Inhouse-Setups</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Postbacks via GTM-Server</li>
                  <li>GA4 für Cross-Channel-Validierung</li>
                  <li>Looker Studio Publisher-Dashboards</li>
                  <li>Brand-Bidding-Monitoring-Tools</li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse einer <em>Affiliate-Marketing-Optimierung.</em></>}
          caption="Affiliate ist ein Disziplin-Kanal — wer nicht aktiv steuert, verliert systematisch.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>Affiliate-Marketing Agentur</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="de" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Alle Performance-Marketing-Disziplinen
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Konkret werden">
          <CTABlock
            headline={<>Affiliate-Marketing Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob Affiliate-Marketing in Ihrem Setup wirtschaftlich Sinn macht — und wenn ja, mit welcher Publisher-Mix-Strategie. Sie bekommen eine ehrliche Einordnung."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Performance-Marketing-Übersicht", to: "/leistungen/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
